import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getRoleLabel } from '../utils/roles';
import {
  getConversations, createConversation, getMessages, sendMessage,
  markConversationRead, getUsers, updateConversation,
  leaveConversation, archiveConversation, unarchiveConversation,
  deleteConversation, transferConversationAdmin, exportConversation,
  loginUser,
} from '../utils/api';
import { formatDate, getInitials, timeAgo } from '../utils/helpers';
import {
  MessageCircle, Send, Plus, Users, Search, Hash, Circle, X, UserPlus, Settings,
  LogOut, Archive, ArchiveRestore, Trash2, Download, Shield, MoreVertical, ChevronDown, ChevronLeft,
  Lock,
} from 'lucide-react';
import Modal from '../components/common/Modal';
import ConfirmModal from '../components/common/ConfirmModal';
import toast from 'react-hot-toast';

const POLL_INTERVAL = 8000; // poll every 8s for new messages

// ── Inline Re-Auth Overlay (messenger popup only) ───────────────────────────
function MessengerReAuth({ onReAuth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      const data = await loginUser(email.trim(), password);
      if (data?.token && data?.user) {
        onReAuth(data.token, data.user, data.refresh_token);
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err?.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(12,22,40,0.95)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--zeus-navy, #0f1a2e)', borderRadius: '16px',
        padding: '32px', width: '340px', maxWidth: '90%',
        border: '1px solid var(--zeus-border, #1e293b)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(212,168,83,0.1)', margin: '0 auto 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Lock size={24} style={{ color: '#d4a853' }} />
          </div>
          <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary, #e2e8f0)' }}>
            Session Expired
          </h3>
          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted, #64748b)', lineHeight: 1.5 }}>
            Sign in again to continue your conversation.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            style={{
              padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--zeus-border, #1e293b)',
              background: 'var(--zeus-navy-light, #151f35)', color: 'var(--text-primary, #e2e8f0)',
              fontSize: '0.88rem', fontFamily: 'inherit', outline: 'none',
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--zeus-border, #1e293b)',
              background: 'var(--zeus-navy-light, #151f35)', color: 'var(--text-primary, #e2e8f0)',
              fontSize: '0.88rem', fontFamily: 'inherit', outline: 'none',
            }}
          />
          {error && (
            <div style={{ fontSize: '0.78rem', color: '#ef4444', textAlign: 'center' }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={loading || !email.trim() || !password}
            style={{
              padding: '11px', borderRadius: '10px', border: 'none',
              background: '#d4a853', color: '#0a0e17', fontWeight: 700,
              fontSize: '0.88rem', cursor: loading ? 'wait' : 'pointer',
              fontFamily: 'inherit', opacity: (loading || !email.trim() || !password) ? 0.5 : 1,
            }}
          >
            {loading ? 'Signing in...' : 'Sign In Again'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function MessengerPage() {
  const { user, sessionExpired, login } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);

  const [searchQ, setSearchQ] = useState('');

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // New conversation modal
  const [showNew, setShowNew] = useState(false);
  const [newType, setNewType] = useState('direct');  // 'direct' | 'group'
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);

  // Conversation actions
  const [showActions, setShowActions] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const actionsRef = useRef(null);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // ── Load conversations ──
  const loadConversations = useCallback(async () => {
    try {
      const data = await getConversations({ archived: showArchived ? 'true' : '' });
      setConversations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingConvs(false);
    }
  }, [showArchived]);

  useEffect(() => {
    loadConversations();
    // Poll for new conversations / unread
    const iv = setInterval(loadConversations, POLL_INTERVAL);
    return () => clearInterval(iv);
  }, [loadConversations]);

  // ── Load messages when active conversation changes ──
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      setLoadingMsgs(true);
      const data = await getMessages(convId);
      setMessages(Array.isArray(data) ? data : []);
      await markConversationRead(convId);
      // Update local unread state
      setConversations(prev => prev.map(c =>
        (c._id === convId) ? { ...c, has_unread: false } : c
      ));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    if (activeConv) {
      loadMessages(activeConv._id);
      // Poll for new messages in active conversation
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => loadMessages(activeConv._id), POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [activeConv, loadMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ──
  const handleSend = async () => {
    if (!msgText.trim() || !activeConv) return;
    setSending(true);
    try {
      const msg = await sendMessage(activeConv._id, { content: msgText.trim() });
      setMessages(prev => [...prev, msg]);
      setMsgText('');
      // Update conversation list's last_message
      setConversations(prev => prev.map(c =>
        c._id === activeConv._id ? {
          ...c,
          last_message: { content: msgText.trim(), sender: user._id, sent_at: new Date() }
        } : c
      ).sort((a, b) => {
        const da = new Date(a.last_message?.sent_at || a.updatedAt || 0);
        const db = new Date(b.last_message?.sent_at || b.updatedAt || 0);
        return db - da;
      }));
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── New conversation ──
  const openNewConversation = async () => {
    try {
      const r = await getUsers();
      const list = Array.isArray(r) ? r : (r?.users || []);
      setTeamUsers(list.filter(u => u._id !== user?._id && u.is_active !== false));
    } catch { setTeamUsers([]); }
    setNewType('direct');
    setNewGroupName('');
    setSelectedUsers([]);
    setShowNew(true);
  };

  const toggleUser = (uid) => {
    setSelectedUsers(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleCreateConversation = async () => {
    if (!selectedUsers.length) return toast.error('Select at least one team member');
    if (newType === 'group' && !newGroupName.trim()) return toast.error('Group name required');

    try {
      const conv = await createConversation({
        type: newType,
        participant_ids: selectedUsers,
        name: newType === 'group' ? newGroupName.trim() : '',
      });
      setShowNew(false);
      await loadConversations();
      setActiveConv(conv);
    } catch (err) {
      toast.error('Failed to create conversation');
    }
  };

  // ── Helpers ──
  const getConvDisplayName = (conv) => {
    if (conv.type === 'group') return conv.name || 'Group Chat';
    const other = conv.participants?.find(p => p.user?._id !== user?._id);
    if (other?.user) return `${other.user.first_name || ''} ${other.user.last_name || ''}`.trim() || other.user.email;
    return 'Direct Message';
  };

  const getConvAvatar = (conv) => {
    if (conv.type === 'group') return <Hash size={18} />;
    const other = conv.participants?.find(p => p.user?._id !== user?._id);
    if (other?.user) return getInitials(other.user.first_name, other.user.last_name);
    return '?';
  };

  const filteredConvs = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(c => {
      const name = getConvDisplayName(c).toLowerCase();
      const lastMsg = c.last_message?.content?.toLowerCase() || '';
      return name.includes(q) || lastMsg.includes(q);
    });
  }, [conversations, searchQ, user]);

  const totalUnread = conversations.filter(c => c.has_unread).length;

  // Close actions menu on outside click
  useEffect(() => {
    const h = (e) => { if (actionsRef.current && !actionsRef.current.contains(e.target)) setShowActions(false); };
    if (showActions) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showActions]);

  // ── Conversation actions ──
  const handleLeave = async () => {
    if (!activeConv) return;
    try {
      await leaveConversation(activeConv._id);
      toast.success('You have left the conversation');
      setActiveConv(null);
      loadConversations();
    } catch (err) { toast.error(err.message || 'Failed to leave'); }
    setConfirmAction(null);
  };

  const handleArchive = async () => {
    if (!activeConv) return;
    try {
      await archiveConversation(activeConv._id);
      toast.success('Conversation archived');
      setActiveConv(null);
      loadConversations();
    } catch (err) { toast.error(err.message || 'Failed to archive'); }
  };

  const handleUnarchive = async () => {
    if (!activeConv) return;
    try {
      await unarchiveConversation(activeConv._id);
      toast.success('Conversation restored');
      setActiveConv(null);
      loadConversations();
    } catch (err) { toast.error(err.message || 'Failed to unarchive'); }
  };

  const handleDelete = async () => {
    if (!activeConv) return;
    try {
      await deleteConversation(activeConv._id);
      toast.success('Conversation deleted');
      setActiveConv(null);
      loadConversations();
    } catch (err) { toast.error(err.message || 'Only the chat admin can delete'); }
    setConfirmAction(null);
  };

  const handleTransferAdmin = async (newAdminId) => {
    if (!activeConv) return;
    try {
      await transferConversationAdmin(activeConv._id, newAdminId);
      toast.success('Admin role transferred');
      setShowTransfer(false);
      loadConversations();
      // Reload active conv to get updated roles
      setActiveConv(prev => prev ? { ...prev, my_role: 'member' } : null);
    } catch (err) { toast.error(err.message || 'Failed to transfer admin'); }
  };

  const handleExport = async () => {
    if (!activeConv) return;
    try {
      await exportConversation(activeConv._id);
      toast.success('Chat history exported');
    } catch (err) { toast.error(err.message || 'Export failed'); }
  };

  const isAdmin = activeConv?.my_role === 'admin';
  const isGroup = activeConv?.type === 'group';
  const isArchived = activeConv?.is_archived;

  // ── Render ──
  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--zeus-black)', position: 'relative' }}>

      {/* Session expired or not authenticated — re-auth inline */}
      {(sessionExpired || !user) && (
        <MessengerReAuth onReAuth={(newToken, userData, refreshToken) => {
          login(newToken, userData, refreshToken);
          // Reload conversations after re-auth
          setTimeout(() => loadConversations(), 300);
        }} />
      )}

      {/* ───── LEFT SIDEBAR ───── */}
      <div style={{
        width: isMobile ? '100%' : '280px',
        minWidth: isMobile ? 0 : '240px',
        flexShrink: 0,
        borderRight: isMobile ? 'none' : '1px solid var(--zeus-border)',
        display: (isMobile && activeConv) ? 'none' : 'flex',
        flexDirection: 'column',
        background: 'var(--zeus-navy)',
      }}>
        {/* Sidebar Header */}
        <div style={{
          padding: '16px 18px', borderBottom: '1px solid var(--zeus-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MessageCircle size={20} style={{ color: 'var(--zeus-gold)' }} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', margin: 0 }}>
              Messages
              {totalUnread > 0 && (
                <span style={{
                  marginLeft: '8px', background: 'var(--status-red)', color: '#fff',
                  borderRadius: '12px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                  fontFamily: 'var(--font-body)',
                }}>
                  {totalUnread}
                </span>
              )}
            </h2>
          </div>
          {!showArchived && (
            <button className="btn btn-ghost btn-sm" onClick={openNewConversation} title="New conversation">
              <Plus size={18} />
            </button>
          )}
        </div>

        {/* Search */}
        <div style={{ padding: '10px 14px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 10px', borderRadius: '10px',
            background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)',
          }}>
            <Search size={14} style={{ color: 'var(--text-muted)' }} />
            <input
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                color: 'var(--text-primary)', fontSize: '0.85rem', flex: 1,
                fontFamily: 'var(--font-body)',
              }}
              placeholder="Search conversations..."
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
          </div>
        </div>

        {/* Active / Archived toggle */}
        <div style={{ display: 'flex', gap: 4, padding: '8px 14px', borderBottom: '1px solid var(--zeus-border)' }}>
          {[
            { key: false, label: 'Active' },
            { key: true, label: 'Archived' },
          ].map(t => (
            <button key={String(t.key)} onClick={() => { setShowArchived(t.key); setActiveConv(null); }}
              style={{
                flex: 1, padding: '5px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: showArchived === t.key ? 700 : 400,
                fontFamily: 'inherit',
                background: showArchived === t.key ? 'var(--zeus-gold-dim)' : 'transparent',
                color: showArchived === t.key ? 'var(--zeus-gold)' : 'var(--text-muted)',
              }}
            >{t.label}</button>
          ))}
        </div>

        {/* Conversation List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingConvs ? (
            <div style={{ padding: '24px', textAlign: 'center' }}><div className="spinner" /></div>
          ) : filteredConvs.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
              <MessageCircle size={32} style={{ marginBottom: '8px', opacity: 0.4 }} />
              <p>{showArchived ? 'No archived conversations' : 'No conversations yet'}</p>
              {!showArchived && (
                <button className="btn btn-primary btn-sm" onClick={openNewConversation} style={{ marginTop: '10px' }}>
                  <Plus size={14} /> Start a conversation
                </button>
              )}
            </div>
          ) : (
            filteredConvs.map(conv => {
              const isActive = activeConv?._id === conv._id;
              const displayName = getConvDisplayName(conv);
              const avatar = getConvAvatar(conv);
              const lastMsg = conv.last_message?.content || '';
              const lastTime = conv.last_message?.sent_at ? timeAgo(conv.last_message.sent_at) : '';
              const participantCount = conv.participants?.length || 0;

              return (
                <div
                  key={conv._id}
                  onClick={() => setActiveConv(conv)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 18px', cursor: 'pointer',
                    background: isActive ? 'var(--zeus-navy-light)' : 'transparent',
                    borderLeft: isActive ? '3px solid var(--zeus-gold)' : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}
                  onMouseOver={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                  onMouseOut={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '50%',
                    background: conv.type === 'group' ? 'var(--zeus-gold-dim)' : 'var(--zeus-navy-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: conv.type === 'group' ? 'var(--zeus-gold)' : 'var(--text-primary)',
                    fontSize: '0.8rem', fontWeight: 600, flexShrink: 0,
                    border: '1px solid var(--zeus-border)',
                  }}>
                    {avatar}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontWeight: conv.has_unread ? 700 : 500,
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {displayName}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', flexShrink: 0, marginLeft: '6px' }}>
                        {lastTime}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginTop: '2px',
                    }}>
                      <span style={{
                        fontSize: '0.8rem',
                        color: conv.has_unread ? 'var(--text-primary)' : 'var(--text-muted)',
                        fontWeight: conv.has_unread ? 500 : 400,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: '200px',
                      }}>
                        {lastMsg || (conv.type === 'group' ? `${participantCount} members` : 'No messages yet')}
                      </span>
                      {conv.has_unread && (
                        <Circle size={9} fill="var(--zeus-gold)" stroke="none" style={{ flexShrink: 0 }} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ───── RIGHT: MESSAGES PANE ───── */}
      <div style={{
        flex: 1, display: (isMobile && !activeConv) ? 'none' : 'flex',
        flexDirection: 'column', background: 'var(--zeus-black)',
      }}>
        {!activeConv ? (
          /* Empty state */
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '12px',
            color: 'var(--text-muted)',
          }}>
            <MessageCircle size={48} style={{ opacity: 0.3 }} />
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '1.2rem' }}>
              {showArchived ? 'Select an archived conversation' : 'Select a conversation'}
            </h3>
            {!showArchived && (
              <>
                <p style={{ fontSize: '0.88rem' }}>or start a new one</p>
                <button className="btn btn-primary btn-sm" onClick={openNewConversation}>
                  <Plus size={14} /> New Conversation
                </button>
              </>
            )}
            {showArchived && (
              <p style={{ fontSize: '0.82rem' }}>View old messages or restore a conversation</p>
            )}
          </div>
        ) : (
          <>
            {/* Message Header */}
            <div style={{
              padding: isMobile ? '10px 14px' : '14px 20px',
              borderBottom: '1px solid var(--zeus-border)',
              display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px',
              background: 'var(--zeus-navy)',
            }}>
              {/* Mobile back button */}
              {isMobile && (
                <button onClick={() => setActiveConv(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', flexShrink: 0 }}>
                  <ChevronLeft size={22} />
                </button>
              )}
              <div style={{
                width: isMobile ? '32px' : '36px', height: isMobile ? '32px' : '36px', borderRadius: '50%',
                background: activeConv.type === 'group' ? 'var(--zeus-gold-dim)' : 'var(--zeus-navy-light)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: activeConv.type === 'group' ? 'var(--zeus-gold)' : 'var(--text-primary)',
                fontSize: '0.75rem', fontWeight: 600, border: '1px solid var(--zeus-border)',
              }}>
                {getConvAvatar(activeConv)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 500, fontSize: isMobile ? '0.9rem' : '1rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getConvDisplayName(activeConv)}
                  </span>
                  {activeConv.my_role === 'admin' && (
                    <Shield size={12} style={{ color: 'var(--zeus-gold)' }} title="Chat Admin" />
                  )}
                  {activeConv.is_archived && (
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: 'rgba(168,85,247,0.12)', color: '#a855f7' }}>Archived</span>
                  )}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {activeConv.type === 'group'
                    ? `${activeConv.participants?.length || 0} members`
                    : activeConv.participants?.find(p => p.user?._id !== user?._id)?.user?.role || ''
                  }
                </div>
              </div>

              {/* Actions kebab menu */}
              <div ref={actionsRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowActions(!showActions)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex' }}>
                  <MoreVertical size={18} />
                </button>
                {showActions && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, zIndex: 50, minWidth: 180,
                    background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
                    borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', overflow: 'hidden',
                  }}>
                    {/* Export */}
                    <button onClick={() => { setShowActions(false); handleExport(); }} style={{
                      width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-primary)', fontSize: '0.82rem', fontFamily: 'inherit', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }} onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseOut={e => e.currentTarget.style.background='none'}>
                      <Download size={14} /> Export Chat History
                    </button>

                    {/* Archive / Unarchive */}
                    {isArchived ? (
                      <button onClick={() => { setShowActions(false); handleUnarchive(); }} style={{
                        width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-primary)', fontSize: '0.82rem', fontFamily: 'inherit', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }} onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseOut={e => e.currentTarget.style.background='none'}>
                        <ArchiveRestore size={14} /> Restore from Archive
                      </button>
                    ) : (
                      <button onClick={() => { setShowActions(false); handleArchive(); }} style={{
                        width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-primary)', fontSize: '0.82rem', fontFamily: 'inherit', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }} onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseOut={e => e.currentTarget.style.background='none'}>
                        <Archive size={14} /> Archive Conversation
                      </button>
                    )}

                    {/* Leave (group only, not archived) */}
                    {isGroup && !isArchived && (
                      <button onClick={() => { setShowActions(false); setConfirmAction('leave'); }} style={{
                        width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                        color: '#f97316', fontSize: '0.82rem', fontFamily: 'inherit', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }} onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseOut={e => e.currentTarget.style.background='none'}>
                        <LogOut size={14} /> Leave Chat
                      </button>
                    )}

                    {/* Transfer Admin (admin + group only) */}
                    {isAdmin && isGroup && !isArchived && (
                      <button onClick={() => { setShowActions(false); setShowTransfer(true); }} style={{
                        width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-primary)', fontSize: '0.82rem', fontFamily: 'inherit', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }} onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseOut={e => e.currentTarget.style.background='none'}>
                        <Shield size={14} /> Transfer Admin
                      </button>
                    )}

                    {/* Delete (admin only) */}
                    {isAdmin && (
                      <>
                        <div style={{ height: 1, background: 'var(--zeus-border)', margin: '2px 0' }} />
                        <button onClick={() => { setShowActions(false); setConfirmAction('delete'); }} style={{
                          width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                          color: '#ef4444', fontSize: '0.82rem', fontFamily: 'inherit', textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }} onMouseOver={e => e.currentTarget.style.background='rgba(239,68,68,0.06)'} onMouseOut={e => e.currentTarget.style.background='none'}>
                          <Trash2 size={14} /> Delete Conversation
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px 10px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {loadingMsgs ? (
                <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" /></div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px', fontSize: '0.88rem' }}>
                  No messages yet. Say hello!
                </div>
              ) : (
                messages.map((msg, i) => {
                  // System messages (join/leave/admin transfer)
                  if (msg.message_type === 'system') {
                    return (
                      <div key={msg._id} style={{
                        textAlign: 'center', padding: '8px 0',
                      }}>
                        <span style={{
                          fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic',
                          background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: 12,
                        }}>
                          {msg.content}
                        </span>
                      </div>
                    );
                  }

                  const isMe = msg.sender?._id === user?._id;
                  const showAvatar = !isMe && (
                    i === 0 || messages[i - 1]?.sender?._id !== msg.sender?._id
                  );
                  const senderName = `${msg.sender?.first_name || ''} ${msg.sender?.last_name || ''}`.trim();

                  return (
                    <div key={msg._id} style={{
                      display: 'flex',
                      flexDirection: isMe ? 'row-reverse' : 'row',
                      gap: '10px',
                      alignItems: 'flex-end',
                      marginTop: showAvatar ? '12px' : '2px',
                    }}>
                      {/* Avatar (only for others, only on first of group) */}
                      {!isMe && (
                        <div style={{ width: '30px', flexShrink: 0 }}>
                          {showAvatar && (
                            <div style={{
                              width: '30px', height: '30px', borderRadius: '50%',
                              background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-primary)',
                            }}>
                              {getInitials(msg.sender?.first_name, msg.sender?.last_name)}
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ maxWidth: isMobile ? '80%' : '65%' }}>
                        {/* Sender name */}
                        {showAvatar && !isMe && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--zeus-gold)', marginBottom: '3px', marginLeft: '4px' }}>
                            {senderName}
                          </div>
                        )}
                        {/* Bubble */}
                        <div style={{
                          padding: '10px 14px',
                          borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: isMe ? 'var(--zeus-gold-dim)' : 'var(--zeus-navy-light)',
                          border: `1px solid ${isMe ? 'rgba(212,168,83,0.2)' : 'var(--zeus-border)'}`,
                          color: 'var(--text-primary)',
                          fontSize: '0.88rem',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {msg.content}
                        </div>
                        {/* Timestamp */}
                        <div style={{
                          fontSize: '0.68rem', color: 'var(--text-muted)',
                          marginTop: '3px',
                          textAlign: isMe ? 'right' : 'left',
                          paddingLeft: isMe ? 0 : '4px',
                          paddingRight: isMe ? '4px' : 0,
                        }}>
                          {msg.createdAt ? timeAgo(msg.createdAt) : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {isArchived ? (
              <div style={{
                padding: isMobile ? '12px 14px' : '14px 20px', borderTop: '1px solid var(--zeus-border)',
                background: 'var(--zeus-navy)', textAlign: 'center',
                fontSize: '0.82rem', color: 'var(--text-muted)',
              }}>
                This conversation is archived. Restore it to send messages.
              </div>
            ) : (
              <div style={{
                padding: isMobile ? '10px 10px' : '14px 20px', borderTop: '1px solid var(--zeus-border)',
                background: 'var(--zeus-navy)',
                display: 'flex', gap: isMobile ? '8px' : '10px', alignItems: 'flex-end',
              }}>
                <textarea
                  style={{
                    flex: 1, minHeight: '42px', maxHeight: '120px', resize: 'none',
                    background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)',
                    borderRadius: '12px', padding: '10px 14px',
                    color: 'var(--text-primary)', fontSize: '0.88rem',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }}
                  placeholder="Type a message..."
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSend}
                  disabled={sending || !msgText.trim()}
                  style={{ height: '42px', width: '42px', padding: 0, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Send size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ───── NEW CONVERSATION MODAL ───── */}
      <Modal isOpen={showNew} onClose={() => setShowNew(false)} title="New Conversation">
        {/* Type Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            className={`btn btn-sm ${newType === 'direct' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setNewType('direct'); setSelectedUsers([]); }}
          >
            <MessageCircle size={14} /> Direct Message
          </button>
          <button
            className={`btn btn-sm ${newType === 'group' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setNewType('group'); setSelectedUsers([]); }}
          >
            <Users size={14} /> Group Chat
          </button>
        </div>

        {/* Group Name */}
        {newType === 'group' && (
          <div className="form-group">
            <label className="form-label">Group Name *</label>
            <input
              className="form-input"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="e.g., PI Team, Settlement Group"
            />
          </div>
        )}

        {/* Team Members */}
        <div className="form-group">
          <label className="form-label">
            {newType === 'direct' ? 'Select team member' : 'Add members'}
          </label>
          <div style={{
            maxHeight: '240px', overflowY: 'auto',
            border: '1px solid var(--zeus-border)', borderRadius: 'var(--radius-md)',
          }}>
            {teamUsers.map(u => {
              const isSelected = selectedUsers.includes(u._id);
              return (
                <div
                  key={u._id}
                  onClick={() => {
                    if (newType === 'direct') setSelectedUsers([u._id]);
                    else toggleUser(u._id);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 14px', cursor: 'pointer',
                    background: isSelected ? 'var(--zeus-gold-dim)' : 'transparent',
                    borderBottom: '1px solid var(--zeus-border)',
                  }}
                >
                  <div className="sidebar-avatar" style={{ width: '32px', height: '32px', fontSize: '0.72rem' }}>
                    {getInitials(u.first_name, u.last_name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                      {u.first_name} {u.last_name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {getRoleLabel(u.role)} · {u.email}
                    </div>
                  </div>
                  {isSelected && (
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: 'var(--zeus-gold)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ color: '#000', fontSize: '0.72rem', fontWeight: 700 }}></span>
                    </div>
                  )}
                </div>
              );
            })}
            {teamUsers.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                No team members found
              </div>
            )}
          </div>
        </div>

        {selectedUsers.length > 0 && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            {selectedUsers.length} selected
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreateConversation}>
            {newType === 'direct' ? 'Start Chat' : 'Create Group'}
          </button>
        </div>
      </Modal>

      {/* ───── CONFIRM LEAVE / DELETE ───── */}
      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmAction === 'leave' ? handleLeave : handleDelete}
        title={confirmAction === 'leave' ? 'Leave Conversation' : 'Delete Conversation'}
        message={confirmAction === 'leave'
          ? 'Are you sure you want to leave this conversation? You will no longer receive messages. Other members will see a notification that you left.'
          : 'Are you sure you want to permanently delete this conversation? All members will lose access. This action cannot be undone.'
        }
        confirmLabel={confirmAction === 'leave' ? 'Leave Chat' : 'Delete'}
        danger
      />

      {/* ───── TRANSFER ADMIN MODAL ───── */}
      <Modal isOpen={showTransfer} onClose={() => setShowTransfer(false)} title="Transfer Chat Admin">
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 14 }}>
          Select a member to make the new chat admin. You will become a regular member.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(activeConv?.participants || [])
            .filter(p => p.user?._id !== user?._id && p.role !== 'admin')
            .map(p => (
              <button key={p.user?._id} onClick={() => handleTransferAdmin(p.user?._id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)',
                  borderRadius: 8, cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: 'inherit',
                  color: 'var(--text-primary)',
                }}
                onMouseOver={e => e.currentTarget.style.borderColor = 'var(--zeus-gold)'}
                onMouseOut={e => e.currentTarget.style.borderColor = 'var(--zeus-border)'}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', background: 'var(--zeus-navy)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: 600, border: '1px solid var(--zeus-border)',
                }}>
                  {getInitials(p.user?.first_name, p.user?.last_name)}
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{p.user?.first_name} {p.user?.last_name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{p.user?.role || p.user?.email}</div>
                </div>
                <Shield size={13} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
              </button>
            ))
          }
        </div>
        <div className="modal-footer" style={{ marginTop: 14 }}>
          <button className="btn btn-secondary" onClick={() => setShowTransfer(false)}>Cancel</button>
        </div>
      </Modal>
    </div>
  );
}
